import { type CardGroupAttributes } from './generated/CardGroupAttributes';
import ApplicationModel, { attr } from '../ApplicationModel';
import { model } from '../modelBuilder';
import ItemPlacement from './ItemPlacement';
import { HasCardLayoutHierarchy } from './concerns/HasCardLayoutHierarchy';
import { JsonQuerySerialization } from './concerns/JsonQuerySerialization';

interface CardGroup extends CardGroupAttributes, HasCardLayoutHierarchy {}

@model
@HasCardLayoutHierarchy
@JsonQuerySerialization
class CardGroup extends ApplicationModel {
  // Start auto-generated
  static typename: 'CardGroup' = 'CardGroup';
  get typename(): 'CardGroup' {
    return 'CardGroup';
  }

  static inputType = 'CardGroupAttributes';

  static fields = {
    id: attr({ writable: true, readable: true }),
    fields: attr.hasMany({ writable: true, readable: true }),
  };
  // End auto-generated

  initialize() {
    this.removeInvalidFields();
  }

  /**
   * Removes any fields from the layout that might be invalid.
   */
  removeInvalidFields() {
    this.fields = this.fields.filter(cardField => cardField.field);
  }

  /**
   * Add a field to the layout
   *
   * @param {CardField} field The field to add
   * @param {import('./ItemPlacement').default} placement The placement in the list for adding the field.
   */
  addField(field, placement = ItemPlacement.bottom()) {
    const index = placement.indexForList(this.fields);
    this.fields.splice(index, 0, field);

    this.cardView.inherited = false;
  }

  /**
   * Removes a CardField by its ID.
   *
   * @param {string} cardFieldId The ID of the CardField to remove
   * @returns {boolean} Whether the field was removed or not.
   */
  removeField(cardFieldId) {
    const index = this.fields.findIndex(field => field.id === cardFieldId);
    if (index > -1) {
      this.fields.splice(index, 1);
      this.cardView.inherited = false;
      return true;
    }

    return false;
  }

  /**
   * Find a CardField by its ID.
   *
   * @param {string} cardFieldId The ID of the CardField
   * @returns {CardField} The CardField that matches the ID
   */
  getFieldById(cardFieldId) {
    return this.fields.find(field => field.id === cardFieldId);
  }

  /**
   * Compares another CardGroup to this, and returns whether they are equal or not.
   *
   * @param {CardGroup} otherGroup The CardGroup to compare.
   */
  isEqualTo(otherGroup) {
    if (this.fields.length !== otherGroup.fields.length) return false;

    return this.fields.every((row, index) =>
      row.isEqualTo(otherGroup.fields[index])
    );
  }
}

export default CardGroup;
